/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { useEffect, useState } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { type SubmitHandler, Controller, useForm } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import input from "../../../../assets/styles/components/input.module.css";
import select from "../../../../assets/styles/components/select.module.css";
import Button from "../../../../components/Button/Button";
import styles from "./FormSupplier.module.css";
import { getCep, getCidade, getCNPJ, getEstado } from "../../../../services/address";
import {
  getSupplier,
  registerOrEditSupplier,
} from "../../../../services/suppliers";
import type { supplierMutate } from "../../../../types/suppliers";

interface SupplierFormData {
  uid_fornecedor?: string;
  razao_social: string;
  nome_fantasia: string;
  cpf_cnpj: string;
  inscricao_estadual?: string;
  telefone?: string;
  whatsapp?: string;
  email?: string;
  cep?: string;
  endereco: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  id_municipio?: number;
  id_estado?: string;
  estado?: {
    estado?: string;
    id_estado?: string;
  };
  cidade?: {
    municipio?: string;
    id_municipio?: number;
  };
  observacao?: string;
  tipo_pessoa?: "fisica" | "juridica";
}

interface EstadoAttributes {
  id_estado: string;
  id_pais?: string;
  estado?: string;
}

interface CidadeAttributes {
  id_municipio: string;
  id_estado?: string;
  municipio?: string;
}

interface ICEPResult {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
}

interface ICNPJResult {
  uf: string;
  cep: string;
  cnpj: string;
  bairro: string;
  email: string;
  numero: string;
  municipio: string;
  logradouro: string;
  complemento: string;
  razao_social: string;
  nome_fantasia: string;
  ddd_telefone_1: string;
}

interface AddSuppliersModalProps {
  uid?: string;
  isEdit?: boolean;
  handleVisible: (fieldName: string, uid: string, e: boolean) => void;
}

const AddSuppliersModal = ({
  uid,
  isEdit,
  handleVisible,
}: AddSuppliersModalProps) => {
  const queryClient = useQueryClient();
  const {
    register,
    reset,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors },
    clearErrors,
    control,
    trigger,
  } = useForm<SupplierFormData>();
  const navigate = useNavigate();
  const [, setSelectedState] = useState("");
  const [, setSelectedCity] = useState("");
  const [, setCities] = useState<CidadeAttributes[]>([]);
  const [, setIsLoadingCities] = useState(false);

  const [cep, setCep] = useState("");
  const [cpnj, setCpnj] = useState("");

  const [cpfCnpjError, setCpfCnpjError] = useState("");

  const isKindPerson = watch("tipo_pessoa");
  const id_estado = watch("id_estado");

  const [supplierQuery, cepQuery, cnpjQuery, estadoQuery, cidadeQuery] =
    useQueries({
      queries: [
        {
          queryKey: ["getSupplier", uid],
          queryFn: () => getSupplier(uid || ""),
          enabled: !!uid,
        },
        {
          queryKey: ["getCep", cep],
          queryFn: () => getCep(cep || ""),
          enabled: !!cep && cep.replace(/\D/g, "").length === 8,
        },
        {
          queryKey: ["getCNPJ", cpnj],
          queryFn: () => getCNPJ(cpnj || ""),
          enabled: isKindPerson === "juridica" && !!cpnj,
        },
        {
          queryKey: ["getEstado"],
          queryFn: () => getEstado(),
          refetchOnWindowFocus: false,
        },
        {
          queryKey: ["getCidade", id_estado],
          queryFn: () => getCidade(id_estado || ""),
          refetchOnWindowFocus: false,
          enabled: !!id_estado,
        },
      ],
    });

  useEffect(() => {
    if (cepQuery.data) {
      const { cep, bairro, complemento, localidade, logradouro, uf } =
        cepQuery.data as ICEPResult;

      reset({
        ...getValues(),
        cep,
        bairro,
        complemento,
        cidade: { municipio: localidade },
        endereco: logradouro,
        id_estado: uf,
      });
    }
  }, [cepQuery.data, isEdit]);

  useEffect(() => {
    if (cnpjQuery.data) {
      const {
        cep,
        bairro,
        complemento,
        municipio,
        logradouro,
        uf,
        razao_social,
        nome_fantasia,
        ddd_telefone_1,
        email,
        numero,
      } = cnpjQuery.data as ICNPJResult;

      reset({
        ...getValues(),
        razao_social,
        nome_fantasia: nome_fantasia || razao_social,
        telefone: formatPhone(ddd_telefone_1 || ""),
        email,
        numero,
        cep,
        bairro,
        complemento,
        id_estado: uf,
        endereco: logradouro,
        cidade: { municipio },
      });
    }
  }, [cnpjQuery.data, isEdit]);

  useEffect(() => {
    if (supplierQuery.data) {
      const data = supplierQuery.data as SupplierFormData;

      reset(data);
    }
  }, [supplierQuery.data, isEdit]);

  useEffect(() => {
    if (cidadeQuery.data && cidadeQuery.data.length > 0) {
      const cidade = watch("cidade.municipio");

      const idCidade = cidadeQuery.data.find(
        (c: CidadeAttributes) =>
          c?.municipio?.toLowerCase() === cidade?.toLowerCase(),
      );

      setValue("id_municipio", idCidade?.id_municipio);
    }
  }, [cidadeQuery.data, isEdit]);

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: supplierMutate) => {
      const res = await registerOrEditSupplier(data);
      return res;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["getSupplier"] });
      toast.success(response.message);

      if (uid) {
        handleVisible("isEdit", "", !isEdit);
      } else {
        navigate(`../edit/${response.data}`, {
          replace: true,
        });
      }
    },
    onError: (error: any) => {
      if (error?.message?.includes("CPF/CNPJ")) {
        setCpfCnpjError("Já existe um fornecedor com este CPF/CNPJ cadastrado");
      } else {
        toast.error(error.message);
      }
    },
  });

  const onSubmit: SubmitHandler<SupplierFormData> = async (data) => {
    const formattedData = {
      ...data,
      cpf_cnpj: data.cpf_cnpj.replace(/\D/g, ""),
      telefone: data.telefone?.replace(/\D/g, ""),
      whatsapp: data.whatsapp?.replace(/\D/g, ""),
      cep: data.cep?.replace(/\D/g, ""),
      id_estado: data.id_estado ? data.id_estado : undefined,
      id_municipio: data.id_municipio ? Number(data.id_municipio) : undefined,
      uid_fornecedor: uid,
      nome_fantasia: removeCharacterInvalid(data.nome_fantasia),
      inscricao_estadual: removeCharacterInvalid(data.inscricao_estadual || ""),
    };

    mutate(formattedData);
  };

  const removeCharacterInvalid = (str: string) => {
    return /^[^a-zA-Z]/.test(str) ? str.replace(/^[^a-zA-Z]+/, "") : str;
  };

  const handleStateChange = async (estadoId: string) => {
    setSelectedState(estadoId);
    setSelectedCity("");
    setValue("id_estado", estadoId);
    setValue("id_municipio", 0);

    if (estadoId) {
      setIsLoadingCities(true);
      try {
        const response = await getCidade(estadoId);
        const cidades = Array.isArray(response)
          ? response
          : Array.isArray(response.data)
            ? response.data
            : [];

        setCities(
          cidades.map((cidade: any) => ({
            id_municipio: cidade.id_municipio || cidade.id,
            municipio: cidade.municipio || cidade.nome,
          })),
        );
      } catch (error) {
        console.error("Erro ao carregar cidades:", error);
        setCities([]);
      } finally {
        setIsLoadingCities(false);
      }
    } else {
      setCities([]);
    }
  };

  const formatCpfCnpj = (value: string) => {
    const cleanedValue = value.replace(/\D/g, "");
    if (isKindPerson === "fisica") {
      return cleanedValue
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    } else if (isKindPerson === "juridica") {
      return cleanedValue
        .replace(/(\d{2})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1/$2")
        .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
    }
    return value;
  };

  const formatPhone = (value: string): string => {
    const numbers = value.replace(/\D/g, "");
    const length = numbers.length;

    if (length === 0) return "";

    if (length >= 11) {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2, 7)}-${numbers.substring(7, 11)}`;
    }

    if (length >= 6) {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2, 6)}-${numbers.substring(6, 10)}`;
    }

    if (length > 2) {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2)}`;
    }

    return numbers;
  };

  // const validateCpfCnpj = (value?: string): true | string => {
  //   const v = (value || "").replace(/\D/g, "");

  //   if (!v) {
  //     return true; // Cpf Cnpjnão é obrigatório
  //   }

  //   if (isKindPerson === "juridica") {
  //     if (v.length !== 14) return "CNPJ inválido";
  //     if (!validarCNPJ(v)) return "CNPJ inválido";
  //   } else {
  //     if (v.length !== 11) return "CPF inválido";
  //     if (!validarCPF(v)) return "CPF inválido";
  //   }

  //   return true;
  // };

  // ── Estilos inline padrão EventoView ─────────────────────────────────────
  const labelS: React.CSSProperties = { fontSize: "0.72rem", color: "#888", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, display: "block" };
  const labelE: React.CSSProperties = { ...labelS, color: "#00b37e" };
  const inpE: React.CSSProperties = { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #00b37e", outline: "none", fontSize: "0.9rem", background: "#f0fff8", boxSizing: "border-box" as const };
  const inpV: React.CSSProperties = { ...inpE, border: "1px solid transparent", background: "transparent", pointerEvents: "none", color: "#222", fontWeight: 500 };
  const errSpan: React.CSSProperties = { color: "#e74c3c", fontSize: "0.78rem", marginTop: 3, display: "block" };
  const grid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px 20px", marginBottom: 20 };
  const divider: React.CSSProperties = { borderTop: "1px solid #eee", margin: "4px 0 20px" };

  const lbl = isEdit ? labelE : labelS;
  const inp = (hasErr?: boolean) => isEdit ? (hasErr ? { ...inpE, border: "1px solid #e74c3c", background: "#fff5f5" } : inpE) : inpV;
  const sel = isEdit ? inpE : { ...inpV, appearance: "none" as const };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>

      {/* Seção 1: Identificação */}
      <div style={grid}>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={errors.razao_social ? { ...lbl, color: "#e74c3c" } : lbl}>Razão Social *</label>
          <input style={inp(!!errors.razao_social)} type="text"
            {...register("razao_social", { required: "Obrigatório" })}
            placeholder={isEdit ? "Razão Social" : ""}
            onBlur={(e) => setValue("nome_fantasia", e.target.value)}
            disabled={!isEdit} />
          {errors.razao_social && <span style={errSpan}>⚠️ {errors.razao_social.message}</span>}
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <label style={lbl}>Nome Fantasia</label>
          <input style={inp()} type="text" {...register("nome_fantasia")} disabled={!isEdit} placeholder={isEdit ? "Nome Fantasia" : ""} />
        </div>
        <div>
          <label style={lbl}>Tipo de Pessoa</label>
          <select style={sel} defaultValue="juridica" {...register("tipo_pessoa", { required: true })} disabled={!isEdit}>
            <option value="fisica">Física</option>
            <option value="juridica">Jurídica</option>
          </select>
        </div>
        <div>
          <label style={lbl}>{isKindPerson === "fisica" ? "CPF" : "CNPJ"}</label>
          <Controller name="cpf_cnpj" control={control} defaultValue=""
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <IMaskInput style={inp(!!errors.cpf_cnpj || !!cpfCnpjError)}
                mask={isKindPerson === "juridica" ? "00.000.000/0000-00" : "000.000.000-00"}
                disabled={!isEdit} value={value as string}
                onAccept={(v: string) => { const formatted = formatCpfCnpj(v); onChange(formatted); if (cpfCnpjError) setCpfCnpjError(""); clearErrors("cpf_cnpj"); }}
                placeholder={!isEdit ? "" : isKindPerson === "juridica" ? "00.000.000/0000-00" : "000.000.000-00"}
                onBlur={() => { onBlur(); trigger("cpf_cnpj"); if (isKindPerson === "juridica") setCpnj((value || "").replace(/\D/g, "")); }}
                inputRef={ref as any} />
            )} />
          {errors.cpf_cnpj && <span style={errSpan}>⚠️ {errors.cpf_cnpj.message as string}</span>}
          {cpfCnpjError && <span style={errSpan}>{cpfCnpjError}</span>}
        </div>
        <div>
          <label style={lbl}>Inscrição Estadual</label>
          <input style={inp()} type="text" {...register("inscricao_estadual")} placeholder={isEdit ? "Inscrição Estadual" : ""} disabled={!isEdit} />
        </div>
      </div>

      <div style={divider} />

      {/* Seção 2: Contato */}
      <div style={grid}>
        <div>
          <label style={lbl}>Telefone</label>
          <input style={inp()} type="tel" {...register("telefone")}
            value={watch("telefone") || ""}
            onChange={(e) => setValue("telefone", formatPhone(e.target.value))}
            placeholder={isEdit ? "(00) 0000-0000" : ""} maxLength={15} disabled={!isEdit} />
        </div>
        <div>
          <label style={lbl}>WhatsApp</label>
          <Controller name="whatsapp" control={control} defaultValue=""
            render={({ field: { onChange, value, ref } }) => (
              <IMaskInput style={inp()} mask="(00) 0 0000-0000" value={value as string}
                onAccept={(v: string) => { onChange(v); clearErrors("whatsapp"); }}
                placeholder={isEdit ? "(00) 00000-0000" : ""} inputRef={ref as any} disabled={!isEdit} />
            )} />
        </div>
        <div>
          <label style={lbl}>Email</label>
          <input style={inp()} type="email" {...register("email")} placeholder={isEdit ? "email@exemplo.com" : ""} disabled={!isEdit} />
        </div>
      </div>

      <div style={divider} />

      {/* Seção 3: Endereço */}
      <div style={grid}>
        <div>
          <label style={lbl}>CEP</label>
          <Controller name="cep" control={control} defaultValue=""
            render={({ field: { onChange, value, ref } }) => (
              <IMaskInput style={inp()} mask="00000-000" value={value as string}
                onAccept={(v: string) => onChange(v)}
                onBlur={() => typeof value === "string" && setCep(value)}
                placeholder={isEdit ? "00000-000" : ""} inputRef={ref as any} disabled={!isEdit} />
            )} />
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <label style={lbl}>Endereço</label>
          <input style={inp()} type="text" placeholder={isEdit ? "Endereço" : ""} {...register("endereco")} disabled={!isEdit} />
        </div>
        <div>
          <label style={lbl}>Número</label>
          <input style={inp()} type="text" {...register("numero")} placeholder={isEdit ? "Número" : ""} disabled={!isEdit} />
        </div>
        <div>
          <label style={lbl}>Complemento</label>
          <input style={inp()} type="text" placeholder={isEdit ? "Complemento" : ""} {...register("complemento")} disabled={!isEdit} />
        </div>
        <div>
          <label style={lbl}>Bairro</label>
          <input style={inp()} type="text" placeholder={isEdit ? "Bairro" : ""} {...register("bairro")} disabled={!isEdit} />
        </div>
        <div>
          <label style={lbl}>Estado</label>
          <select style={sel} {...register("id_estado")} onChange={(e) => handleStateChange(e.target.value)} disabled={!isEdit}>
            <option value="">Selecione o Estado</option>
            {estadoQuery?.data?.length > 0 && estadoQuery.data?.map((state: EstadoAttributes) => (
              <option key={state.id_estado} value={state.id_estado}>{state.estado}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={lbl}>Município</label>
          <select style={sel} {...register("id_municipio")} disabled={!isEdit ? true : !cidadeQuery?.data?.length}>
            <option value="">Selecione o Município</option>
            {cidadeQuery?.data?.length > 0 && cidadeQuery.data.map((city: CidadeAttributes) => (
              <option key={city.id_municipio} value={city.id_municipio}>{city.municipio}</option>
            ))}
          </select>
        </div>
      </div>

      {isEdit && (
        <div className={styles.buttonSave}>
          <Button variant="contain" type="submit">
            {isPending ? "Salvando..." : "Salvar"}
          </Button>
          {uid && isEdit && (
            <Button variant="contain" typeStyle={["cancel"]} type="button"
              onClick={() => handleVisible("isEdit", "", !isEdit)}>
              Cancelar
            </Button>
          )}
        </div>
      )}
    </form>
  );
};

export default AddSuppliersModal;